import { useEffect, useState } from 'react';
import { showNotification } from '../../../components/snackbar/saga';
import store from '../../../store';
import { commonTranslations } from '../../../translation/commonTranslations';
import { apiClient } from '../../../utilities/apiClient';
import { getCurrentUserId } from '../../../utilities/auth';
import {
  GET_USER_LOADING,
  GET_USER_SUCCESS,
  GET_USER_FAILED,
  LOGOUT_USER,
} from './types';

// Phase A: auth lives on the NestJS backend. Firestore is still used for
// profile fields that the marketplace pages read/write (those move to
// Postgres in Phase B). We expose a back-compat shape on GET_USER_SUCCESS so
// existing components keep reading `user.contactNumber`, `user.isAnonymousUser`,
// etc., without per-component rewrites.
function normalizeUser(user) {
  if (!user) return user;
  return {
    ...user,
    // Legacy field aliases — Phase A users-shape compat.
    contactNumber: user.phone ?? null,
    isAnonymousUser: user.isAnonymous,
    pass: '',
  };
}

// `signUpUser` was email+password against Firebase. With passwordless email,
// signup IS the magic-link/OTP request. The legacy form fields (name, phone)
// are dropped; collect those on the profile page after signin.
export const signUpUser = ({ email, name, phone }) => {
  void name;
  void phone;
  return requestEmailLogin({ email });
};

// Passwordless email entry — request a magic link + 6-digit OTP, both
// delivered together by the cPanel SMTP queue. Surfaces a notification so
// the user knows to check their inbox.
export const requestEmailLogin = ({ email, locale = 'en' }) =>
  apiClient
    .post('/auth/email/request', { email, locale })
    .then(() => {
      showNotification({ message: 'Check your email for the sign-in link and code' });
    })
    .catch((e) => {
      showNotification({
        isError: true,
        message: e?.response?.data?.message || e?.message || 'request failed',
      });
      throw e;
    });

export const verifyEmailCode = ({ email, code, name }) => {
  const trimmedName = typeof name === 'string' ? name.trim() : '';
  return apiClient
    .post('/auth/email/verify', {
      email,
      code,
      ...(trimmedName ? { name: trimmedName } : {}),
    })
    .then((res) => {
      store.dispatch({ type: GET_USER_SUCCESS, data: normalizeUser(res.data.user) });
      return res.data.user;
    });
};

// PATCH /auth/me — backend's allow-list filters to editable fields
// (name, userImage, description, facebook, instagram, twitter, tiktok,
// isProfileCompleted). Other keys in `userData` are silently dropped.
export const updateUserProfile = ({ userId, userData, notification = true, noUpdate = false }) => {
  void userId; // server identifies the user from the access cookie
  return apiClient
    .patch('/auth/me', userData)
    .then(() => {
      if (!noUpdate) {
        getUser({});
      }
      if (notification) {
        showNotification({ message: 'Success!' });
      }
    })
    .catch((e) => {
      if (!noUpdate) {
        store.dispatch({ type: GET_USER_FAILED });
      }
      if (notification) {
        showNotification({
          isError: true,
          message: e?.response?.data?.message || e?.message || 'update failed',
        });
      }
      throw e;
    });
};

export const fetchUser = async () => {
  const id = getCurrentUserId();
  return id ? { uid: id } : null;
};

export const logoutUser = (noReload = false) => {
  apiClient
    .post('/auth/logout')
    .catch(() => null)
    .finally(() => {
      store.dispatch({ type: LOGOUT_USER });
      // Mint a fresh anonymous session so the SPA never sits with no identity.
      apiClient.post('/auth/anonymous').catch(() => null);
      if (!noReload) {
        window.location.reload();
      }
    });
};

// Replaces the Firestore getDoc(users/{uid}) check. Hits GET /auth/me on the
// new backend. Anonymous, blocked, and deactivated users still resolve to
// GET_USER_FAILED so existing reducer logic (and routes.js PrivateRoute) keep
// working unchanged.
export const getUser = ({ userId: _userId, errCallback = () => null } = {}) => {
  store.dispatch({ type: GET_USER_LOADING });
  apiClient
    .get('/auth/me')
    .then((res) => {
      const user = res.data.user;
      if (!user) {
        store.dispatch({ type: GET_USER_FAILED });
        errCallback();
        return;
      }
      if (user.isAnonymous) {
        store.dispatch({ type: GET_USER_FAILED });
        return;
      }
      if (user.isBlocked) {
        logoutUser(true);
        showNotification({ isError: true, message: 'The user has been blocked' });
        store.dispatch({ type: GET_USER_FAILED });
        return;
      }
      if (user.isDeactivated) {
        logoutUser(true);
        showNotification({ isError: true, message: 'The user has been deactivated' });
        store.dispatch({ type: GET_USER_FAILED });
        return;
      }
      store.dispatch({ type: GET_USER_SUCCESS, data: normalizeUser(user) });
    })
    .catch((e) => {
      store.dispatch({ type: GET_USER_FAILED });
      if (e?.response?.status === 401) {
        errCallback();
      }
    });
};

export const onUserVote = ({ userData, country }) => {
  void userData; // server identifies the user from the access cookie
  return apiClient
    .post('/auth/votes', { country })
    .then(() => {
      showNotification({ isError: false, message: commonTranslations.votePlaced });
    })
    .catch((e) => {
      showNotification({
        isError: true,
        message: e?.response?.data?.message || e?.message || 'vote failed',
      });
    });
};

// useFirebaseUser previously listened to Firebase auth state. With the new
// backend, identity is the JWT in a cookie — synchronously readable via the
// uid cookie. Hook is preserved for callers that already destructure
// `{ user }`.
export const useFirebaseUser = () => {
  const [user, setUser] = useState();

  useEffect(() => {
    const id = getCurrentUserId();
    if (id) {
      setUser({ uid: id });
    }
  }, []);

  return { user };
};
